import { addClinicDays, clinicDateAt, clinicDateStringFromInstant } from "@/lib/clinicTime";

const ACTIVE_CONFLICT_STATES = new Set(["patient_waiting", "in_treatment", "reserved"]);
const CLOSED_STATUSES = new Set(["Cancelled", "Rescheduled", "No Show"]);

export const CHAIR_BOARD_STATES = {
  patient_waiting: {
    label: "Patient waiting",
    caption: "Checked in before chair time",
    tone: "amber",
  },
  in_treatment: {
    label: "Checked in",
    caption: "Chair is occupied",
    tone: "teal",
  },
  reserved: {
    label: "Reserved",
    caption: "Slot is protected",
    tone: "sky",
  },
  checkout: {
    label: "Checkout",
    caption: "Clinical work is done",
    tone: "violet",
  },
  needs_turnover: {
    label: "Needs turnover",
    caption: "Clean and reset chair",
    tone: "rose",
  },
  ready_for_next: {
    label: "Ready",
    caption: "Can absorb work",
    tone: "emerald",
  },
  blocked: {
    label: "Blocked",
    caption: "Unavailable",
    tone: "slate",
  },
};

export function buildChairBoard({
  chairs = [],
  appointments = [],
  now = new Date(),
  day = now,
  turnoverMinutes = 20,
} = {}) {
  const nowDate = asDate(now);
  const dayStart = startOfDay(asDate(day));
  const dayEnd = addDays(dayStart, 1);
  const appointmentsByChair = groupAppointmentsByChair(appointments, dayStart, dayEnd);

  const rows = chairs.map((chair) => {
    const chairAppointments = appointmentsByChair.get(chair.id || null) || [];
    return buildChairRow(chair, chairAppointments, nowDate, turnoverMinutes);
  });

  const doctorConflicts = findDoctorConflicts(rows);
  const conflictDoctorIds = new Set(doctorConflicts.map((c) => c.doctorId));
  const conflictRows = rows.map((row) => ({
    ...row,
    hasDoctorConflict: row.doctorId ? conflictDoctorIds.has(row.doctorId) : false,
  }));

  return {
    chairs: conflictRows,
    doctorConflicts,
    summary: summarizeRows(conflictRows),
  };
}

function buildChairRow(chair, appointments, now, turnoverMinutes) {
  const active = appointments.find((appt) => isActiveAt(appt, now));
  const waiting = appointments.find((appt) => isWaitingAt(appt, now));
  const checkout = appointments.find((appt) => isCheckoutHandoff(appt));
  const recentCompleted = appointments.find((appt) => needsTurnover(appt, now, turnoverMinutes));
  const nextAppointment = appointments.find((appt) => asDate(appt.scheduled_start) > now);
  const focus = waiting || checkout || active || recentCompleted || nextAppointment || null;

  let state = "ready_for_next";
  if (waiting) state = "patient_waiting";
  else if (checkout) state = "checkout";
  else if (active) state = stateForActiveAppointment(active);
  else if (recentCompleted) state = "needs_turnover";
  else if (nextAppointment) state = "reserved";

  return {
    chairId: chair.id || null,
    chairName: chair.name || "Appointments",
    chairCapabilities: chair.chair_capabilities || chair.capabilities || [],
    state,
    stateMeta: CHAIR_BOARD_STATES[state],
    activeAppointment: focus ? appointmentSummary(focus) : null,
    nextAppointment: nextAppointment ? appointmentSummary(nextAppointment) : null,
    doctorId: focus?.doctor_id || null,
    doctorName: focus?.doctor_name || null,
    openUntil: nextAppointment ? asDate(nextAppointment.scheduled_start) : null,
    hasDoctorConflict: false,
  };
}

function stateForActiveAppointment(appt) {
  const status = normalizeStatus(appt.status);
  if (status === "Checked In" && appt.doctor_finished_at) return "checkout";
  if (status === "Checked In") return "in_treatment";
  if (status === "Completed") return "checkout";
  if (status === "Pencil" || status === "Scheduled" || status === "Confirmed") return "reserved";
  return "reserved";
}

function appointmentSummary(appt) {
  return {
    id: appt.id,
    patientName: appt.patient_name || "Unnamed patient",
    doctorName: appt.doctor_name || null,
    doctorId: appt.doctor_id || null,
    status: normalizeStatus(appt.status),
    start: asDate(appt.scheduled_start),
    end: asDate(appt.scheduled_end),
    walkIn: Boolean(appt.walk_in),
  };
}

function groupAppointmentsByChair(appointments, dayStart, dayEnd) {
  const grouped = new Map();
  for (const appt of appointments) {
    const status = normalizeStatus(appt.status);
    if (CLOSED_STATUSES.has(status)) continue;
    const start = asDate(appt.scheduled_start);
    const end = asDate(appt.scheduled_end);
    if (!start || !end || end <= dayStart || start >= dayEnd) continue;
    const key = appt.chair_id || null;
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key).push(appt);
  }
  for (const rows of grouped.values()) {
    rows.sort((a, b) => asDate(a.scheduled_start) - asDate(b.scheduled_start));
  }
  return grouped;
}

function findDoctorConflicts(rows) {
  const activeByDoctor = new Map();
  for (const row of rows) {
    if (!row.doctorId || !ACTIVE_CONFLICT_STATES.has(row.state)) continue;
    if (!activeByDoctor.has(row.doctorId)) {
      activeByDoctor.set(row.doctorId, {
        doctorId: row.doctorId,
        doctorName: row.doctorName,
        chairIds: [],
      });
    }
    activeByDoctor.get(row.doctorId).chairIds.push(row.chairId);
  }
  return [...activeByDoctor.values()]
    .filter((group) => group.chairIds.length > 1)
    .map((group) => ({
      ...group,
      chairIds: group.chairIds.sort(),
    }));
}

function summarizeRows(rows) {
  return rows.reduce((summary, row) => {
    if (row.state === "in_treatment") summary.inTreatment += 1;
    if (row.state === "patient_waiting") summary.waiting += 1;
    if (row.state === "checkout") summary.checkout += 1;
    if (row.state === "needs_turnover") summary.needsTurnover += 1;
    if (row.state === "ready_for_next") summary.ready += 1;
    if (row.hasDoctorConflict) summary.doctorConflicts += 1;
    return summary;
  }, {
    inTreatment: 0,
    waiting: 0,
    checkout: 0,
    needsTurnover: 0,
    ready: 0,
    doctorConflicts: 0,
  });
}

function isActiveAt(appt, now) {
  const status = normalizeStatus(appt.status);
  if (status === "Checked In" && Boolean(appt.checked_in_at) && !appt.doctor_finished_at) {
    const start = asDate(appt.scheduled_start);
    return start <= now;
  }
  const start = asDate(appt.scheduled_start);
  const end = asDate(appt.scheduled_end);
  return start <= now && now < end;
}

function isWaitingAt(appt, now) {
  const status = normalizeStatus(appt.status);
  const start = asDate(appt.scheduled_start);
  return status === "Checked In" && Boolean(appt.checked_in_at) && !appt.doctor_finished_at && now < start;
}

function isCheckoutHandoff(appt) {
  const status = normalizeStatus(appt.status);
  return status === "Checked In" && Boolean(appt.checked_in_at) && Boolean(appt.doctor_finished_at);
}

function needsTurnover(appt, now, turnoverMinutes) {
  const status = normalizeStatus(appt.status);
  const end = asDate(appt.scheduled_end);
  if (status !== "Completed" || !end || end > now) return false;
  return now.getTime() - end.getTime() <= turnoverMinutes * 60 * 1000;
}

function normalizeStatus(status) {
  if (status === "CheckedIn") return "Checked In";
  if (status === "NoShow") return "No Show";
  return status || "Scheduled";
}

function asDate(value) {
  if (value instanceof Date) return value;
  return value ? new Date(value) : null;
}

function startOfDay(d) {
  return clinicDateAt(clinicDateStringFromInstant(d), 0, 0, 0);
}

function addDays(d, n) {
  return clinicDateAt(addClinicDays(clinicDateStringFromInstant(d), n), 0, 0, 0);
}
